import Axios from "axios";
import { EventEmitter } from "./event.service";
import { getLoginToken, clearLoginToken, setLoginToken } from "./storage";
import { firebase_auth } from "./firebase-config";
import { shouldSuppressForcedLogout } from "../utils/sessionDeviceInfo";
import { logAuthSession } from "../utils/authSessionDebug";
const axiosInstance = Axios.create();

// Axios instance for non-critical requests (e.g. Stripe quantity sync) — never triggers logout on error
const axiosNoLogout = Axios.create();
function isCloudFunctionsRequest(url) {
  if (typeof url !== "string") return false;
  return (
    url.includes("cloudfunctions.net") ||
    url.includes("/cloudfunctions-stage/") ||
    url.includes("/cloudfunctions-uat/") ||
    url.includes("/cloudfunctions-dev/") ||
    url.includes("/cloudfunctions-prod/")
  );
}

function isSendEmailDataRequest(url) {
  return typeof url === "string" && url.includes("sendEmailData");
}

function resolveCompanyIdForPayload() {
  if (typeof window === "undefined") return "";
  return (
    localStorage.getItem("companyId") ||
    localStorage.getItem("userId") ||
    ""
  );
}

function injectCompanyIdForEmailRequest(config) {
  if (!isSendEmailDataRequest(config?.url)) return config;
  const payload = config?.data;
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return config;
  }
  if (payload.companyId || payload.companyID) return config;

  const resolvedCompanyId = resolveCompanyIdForPayload();
  if (!resolvedCompanyId) return config;

  config.data = {
    ...payload,
    companyId: resolvedCompanyId,
  };
  return config;
}

async function getFreshAuthToken() {
  const currentUser = firebase_auth?.currentUser;
  if (currentUser?.getIdToken) {
    try {
      const freshToken = await currentUser.getIdToken();
      if (freshToken) {
        setLoginToken(freshToken);
        return freshToken;
      }
    } catch (error) {
      logAuthSession("token_refresh_failed", {
        message: error?.message,
      });
      console.warn("Failed to refresh Firebase auth token:", error?.message);
    }
  }

  return getLoginToken();
}

axiosNoLogout.interceptors.request.use(
  async (config) => {
    const token = await getFreshAuthToken();
    const isCloudFunction = isCloudFunctionsRequest(config.url);
    if (token && isCloudFunction && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return injectCompanyIdForEmailRequest(config);
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.request.use(
  async (config) => {
    const isCloudFunction = isCloudFunctionsRequest(config.url);
    if (isCloudFunction && !apiAuthGateOpen) {
      return Promise.reject(
        Object.assign(new Error("API auth gate closed"), { code: "AUTH_GATE_CLOSED", config })
      );
    }
    const token = await getFreshAuthToken();
    if (token && isCloudFunction && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return injectCompanyIdForEmailRequest(config);
  },
  (error) => Promise.reject(error)
);

// URLs that must never trigger logout on error (401, 500, etc.)
// Stripe/billing failures are usually config or Stripe-side — not invalid Firebase session.
const SKIP_LOGOUT_URL_PATTERNS = [
  "stripeProxy",
  "getStripeConfig",
  "createSubscription",
  "stripeProxyUpdateSubscriptionQuantity",
];

function shouldSkipLogoutForRequest(requestUrl) {
  if (typeof requestUrl !== "string") return false;
  return SKIP_LOGOUT_URL_PATTERNS.some((pattern) => requestUrl.includes(pattern));
}

/** After a confirmed 401, block new Cloud Function calls until next login. */
let apiAuthGateOpen = true;
let logoutDispatchScheduled = false;

export function resetApiAuthGate() {
  apiAuthGateOpen = true;
  logoutDispatchScheduled = false;
}

function closeApiAuthGate() {
  apiAuthGateOpen = false;
}

function dispatchLogoutOnce(requestUrl) {
  if (logoutDispatchScheduled) return;
  if (shouldSuppressForcedLogout()) {
    logAuthSession("forced_logout_suppressed", {
      trigger: "axios_401",
      url: requestUrl,
    });
    return;
  }
  logoutDispatchScheduled = true;
  closeApiAuthGate();
  logAuthSession("forced_logout", {
    trigger: "axios_401",
    url: requestUrl,
  });
  EventEmitter.dispatch("logoutUser");
  clearLoginToken();
  window.location.href = "/login";
}

async function tryRetryCloudFunction401(error) {
  const status = error?.response?.status;
  const requestUrl = error?.config?.url || "";
  const alreadyRetried = error?.config?._authRetried === true;

  if (status !== 401 || alreadyRetried || !isCloudFunctionsRequest(requestUrl)) {
    return null;
  }

  const currentUser = firebase_auth?.currentUser;
  if (!currentUser?.getIdToken) return null;

  try {
    const refreshedToken = await currentUser.getIdToken(true);
    if (!refreshedToken) return null;

    setLoginToken(refreshedToken);
    return axiosInstance.request({
      ...error.config,
      _authRetried: true,
      headers: {
        ...(error.config?.headers || {}),
        Authorization: `Bearer ${refreshedToken}`,
      },
    });
  } catch (refreshError) {
    console.warn("Cloud Function 401 retry after token refresh failed:", refreshError?.message);
    return null;
  }
}

const errorHandler = (error) => {
  const skipAuthRedirect = error?.config?.skipAuthRedirect === true;
  const requestUrl = error?.config?.url || "";
  if (skipAuthRedirect || shouldSkipLogoutForRequest(requestUrl)) return;
  if (error?.code === "AUTH_GATE_CLOSED") return;
  const token = getLoginToken();
  if (token !== "" && error?.response?.status === 401) {
    dispatchLogoutOnce(requestUrl);
  }
};

axiosInstance.interceptors.response.use(
  (config) => {
    return config;
  },
  async (error) => {
    const requestUrl = error?.config?.url || "";
    const status = error?.response?.status;
    const isEmail403 = isSendEmailDataRequest(requestUrl) && status === 403;
    const alreadyRetried = error?.config?._emailAuthRetried === true;

    if (isEmail403 && !alreadyRetried) {
      try {
        const refreshedToken = firebase_auth?.currentUser?.getIdToken
          ? await firebase_auth.currentUser.getIdToken(true)
          : "";

        if (refreshedToken) {
          setLoginToken(refreshedToken);
          const retryConfig = {
            ...error.config,
            _emailAuthRetried: true,
            headers: {
              ...(error.config?.headers || {}),
              Authorization: `Bearer ${refreshedToken}`,
            },
          };
          return axiosInstance.request(retryConfig);
        }
      } catch (refreshError) {
        console.warn("sendEmailData retry after token refresh failed:", refreshError?.message);
      }
    }

    const retried = await tryRetryCloudFunction401(error);
    if (retried) return retried;

    errorHandler(error);
    return Promise.reject(error);
  }
);

export default axiosInstance;
export { axiosNoLogout };
